import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { PromptInput } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { PromptDetailSaveEvent } from '../models/prompt-detail-form-submit-event';
import { PromptDetailSaveModel } from '../models/prompt-detail-save.model';
import { deleteFileMutation } from '../queries/delete-file.graphql';
import { deletePromptMutation } from '../queries/delete-prompt.graphql';
import { DeleteFile, DeleteFileVariables } from '../queries/generated/DeleteFile';
import { DeletePromptVariables } from '../queries/generated/DeletePrompt';
import { SavePrompt, SavePromptVariables } from '../queries/generated/SavePrompt';
import { SavePromptMutation } from '../queries/save-prompt.graphql';

@Injectable({ providedIn: 'root' })
export class PromptDetailApiService {
   constructor(private apiService: LcsApiService, private http: HttpClient) {}

   save(
      promptDetail: PromptDetailSaveModel,
      fields: (keyof PromptInput)[],
      file: File | null
   ): Observable<ExecutionResult<SavePrompt>> {
      if (file) {
         const body: { query: string; variables: SavePromptVariables } = {
            query: SavePromptMutation,
            variables: {
               prompt: { ...promptDetail, fields: [...fields, 'file.accountId', 'file.metaTag', 'file.promptType'] },
            },
         };
         const formData = new FormData();
         formData.append('request', JSON.stringify(body));
         formData.append(file.name, file);

         return this.http.post<ExecutionResult<SavePrompt>>(this.apiService.graphApiUrl, formData);
      } else {
         return this.apiService.query<SavePrompt, SavePromptVariables>({
            query: SavePromptMutation,
            variables: {
               prompt: {
                  ...promptDetail,
                  fields: fields,
               },
            },
         });
      }
   }

   revertPromptFileUpdate(prompt: PromptDetailSaveEvent): Observable<ExecutionResult<SavePrompt>> {
      const extendedPrompt = prompt as {
         prompt: PromptDetailSaveModel & {
            promptFields?: any;
            voiceMailGreetingPrompt?: any;
            routeToNewPromptAfterSave?: any;
         };
      };

      const {
         prompt: { promptFields, routeToNewPromptAfterSave, voiceMailGreetingPrompt, ...cleanedPrompt },
      } = extendedPrompt;

      const fields: (keyof PromptInput)[] = ['fileId'];

      return this.apiService.query<SavePrompt, SavePromptVariables>({
         query: SavePromptMutation,
         variables: {
            prompt: {
               ...cleanedPrompt,
               fields,
            },
         },
      });
   }

   deletePrompt(promptId: number): Observable<ExecutionResult<void>> {
      return this.apiService.query<void, DeletePromptVariables>({
         query: deletePromptMutation,
         variables: {
            promptId,
         },
      });
   }

   deleteFile(fileId: number | null): Observable<ExecutionResult<DeleteFile>> {
      return this.apiService.query<DeleteFile, DeleteFileVariables>({
         query: deleteFileMutation,
         variables: {
            fileId: fileId ?? 0,
         },
      });
   }
}
